import { auditReports } from '@mi-front/api-clients';
import { useEffect, useRef } from 'react';

interface UseOrderReprocessProps {
  orders: string[];
  reprocessKey?: string;
  onReprocessComplete?: () => void;
  onReprocessError?: (error: Error) => void;
}

export function useOrderReprocess({
  orders,
  reprocessKey,
  onReprocessComplete,
  onReprocessError,
}: UseOrderReprocessProps) {
  // Join orders if multiple, otherwise return single order
  const currentOrders = orders.length > 1 ? orders.join(',') : orders[0] || '';

  // Track previous status to detect transitions
  const previousStatusRef = useRef<auditReports.PublicJobDtoStatusEnumKey | undefined>(undefined);

  // Mutation for triggering reprocess
  const reprocessMutation = auditReports.useTriggerReprocessOlafReprocess({
    mutation: {
      onError: (error: Error) => {
        onReprocessError?.(error);
      },
    },
  });

  // Shared query options
  const shouldEnableQuery = !reprocessMutation.submittedAt || reprocessMutation.isSuccess;
  const refetchInterval = (query: { state: { data?: { status?: string } } }) =>
    query.state.data?.status === auditReports.PublicJobDtoStatusEnum.WAITING ||
    query.state.data?.status === auditReports.PublicJobDtoStatusEnum.IN_PROGRESS
      ? 2000
      : false;

  // Query for checking job status - use reprocessKey if provided, otherwise use orderId
  const statusQueryByReprocessKey = reprocessKey
    ? auditReports.useGetOlafReprocessJobStatusByReprocessKey(reprocessKey, {
        query: {
          enabled: Boolean(reprocessKey) && shouldEnableQuery,
          refetchInterval,
        },
      })
    : null;

  const statusQueryByOrderId = !reprocessKey
    ? auditReports.useGetOlafReprocessJobStatusByOrderId(currentOrders, {
        query: {
          enabled: Boolean(currentOrders) && shouldEnableQuery,
          refetchInterval,
        },
      })
    : null;

  const statusQuery = statusQueryByReprocessKey || statusQueryByOrderId;

  // Detect status transition from IN_PROGRESS/WAITING to COMPLETED
  useEffect(() => {
    const currentStatus = statusQuery?.data?.status;
    const previousStatus = previousStatusRef.current;

    // Check if status transitioned from IN_PROGRESS or WAITING to COMPLETED
    if (
      currentStatus === auditReports.PublicJobDtoStatusEnum.COMPLETED &&
      previousStatus === auditReports.PublicJobDtoStatusEnum.IN_PROGRESS
    ) {
      onReprocessComplete?.();
    }

    // Update previous status ref
    if (currentStatus) {
      previousStatusRef.current = currentStatus;
    }
  }, [statusQuery?.data?.status, onReprocessComplete]);

  return {
    reprocessMutation,
    statusQuery: statusQuery!,
    currentOrders,
    totalOrders: orders.length,
  };
}
